import {
  ElementType,
  PropsWithChildren,
  ReactNode,
  HTMLAttributes,
} from 'react';
import { clsx } from 'clsx';

import { themes } from '@monoimin/sb-types';

import { newRenderWithMarks } from '../../utils';
import { flagSizes } from '../image/Flag';

type TypographyComponentType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'p'
  | 'span'
  | 'small'
  | 'div'
  | 'button'
  | 'address';

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  as?: TypographyComponentType;
  theme?: themes;
  flagSize?: flagSizes;
  flagClassName?: string;
  anchorCopyTooltip?: ReactNode;
  className?: string;
  id?: string;
  dataTestId?: string;
}

// Todo добавить ссылку на фигму, обяснить почему такие стили
const flagClassNames: Record<'h1' | 'h2' | 'h3' | string, string> = {
  h1: 'mr-3',
  h2: 'mb-[7px] mr-3',
  h3: 'mr-2',
};

const flagSizesParam: Record<'h1' | 'h2' | 'h3' | string, flagSizes> = {
  h1: 'large',
  h2: 'medium',
  h3: 'small',
};
const classNameParam: Record<'h1' | 'h2' | 'h3' | string, string> = {
  h1: 'h1 text-balance',
  h2: 'h2 text-balance',
  h3: 'h3 text-balance',
};

/**
 * Компонент `NewTypography` для отображения текста с различными стилями и возможностью копирования ссылки.
 *
 * @param {ReactNode} children - Дочерние элементы, которые будут отображаться внутри компонента.
 * @param {TypographyComponentType} [] - Тип HTML-элемента, который будет использоваться для отображения текста.
 * @param {themes} [theme='default'] - Тема для компонента, которая будет применяться через атрибут `data-theme`.
 * @param {flagSizes} [flagSize='medium'] - Размер флага, который будет отображаться рядом с текстом.
 * @param {string} [flagClassName] - Дополнительный класс для флага.
 * @param {ReactNode} [anchorCopyTooltip] - Элемент тултипа для копирования ссылки.
 * @param {string} [className] - Дополнительный класс для компонента.
 * @param {string} [id] - Идентификатор элемента.
 * @param {HTMLAttributes<HTMLElement>} attributes - Дополнительные HTML-атрибуты, которые будут применены к компоненту.
 *
 * @returns {JSX.Element} Компонент `NewTypography`.
 */
export const Typography = ({
  children,
  as = 'p',
  theme = 'default',
  flagSize = 'medium',
  flagClassName,
  anchorCopyTooltip,
  className,
  id,
  dataTestId,
  ...attributes
}: PropsWithChildren<TypographyProps>): JSX.Element => {
  const Component = as as ElementType;

  return (
    <Component
      {...attributes}
      className={clsx('typography', className, classNameParam[as], {
        // если нет тултипа, соответственно рендерится не заголовок, а обычный текст, то не рендерим классы для заголовка
        'scroll-mt-24 group/heading relative cursor-pointer hover:opacity-90 duration-150':
          id && anchorCopyTooltip,
        body: !(classNameParam[as] || className),
      })}
      id={id}
      // если нет тултипа, и стиль флага не один из h1,h2,h3, то есть рендерится обычный текст, то не рендерим айдишник anchor-title
      data-testid={id && anchorCopyTooltip ? 'anchor-title' : dataTestId}
      data-atom-name='typography'
      // тему передаем в атрибут data-theme, стиль меняется в зависимости от темы на урвоне css
      data-theme={theme}
    >
      {anchorCopyTooltip}
      {newRenderWithMarks({
        children,
        size: flagSizesParam[as] || flagSize,
        className: clsx(flagClassNames[as], flagClassName),
      })}
    </Component>
  );
};
